import React from 'react';
import { getRandomInt } from '@/utils';
import type {
    Cluster as ClusterFields,
    Panel as PanelFields,
} from '@/generated/graphql';
import { ClusterShape } from '@/generated/graphql';
import type { PanelProps } from '@/components/stage/Panel/Panel';

type ClusterConfig = {
    gridRange: number;
    maxDivisor: number;
    cellSize: number;
    gridSize: number;
};

export type ClusterOptions = Record<string, never>;
export type ClusterCreateObject = {
    clusterFields: ClusterFields;
    clusterStyles: React.CSSProperties;
    panels: PanelProps[];
    panelsTotal: number;
    clusterConfig: ClusterConfig;
};

const clusterCreateObject = (
    clusterFields: ClusterFields,
    options: ClusterOptions = {},
): ClusterCreateObject => {
    const forceRandom = true; // TODO: to be configurable

    const clusterShape: ClusterShape =
        clusterFields.shape || ClusterShape.Float;
    const floatingCoords = clusterShape === ClusterShape.Float;
    const panelsTotal = clusterFields.panels.length;
    const gridRange = panelsTotal < 4 ? 2 : Math.floor(Math.sqrt(panelsTotal));
    const maxDivisor = 40; // TODO: this could be configurable
    const cellSize = 300; // TODO: Make dynamic. ATM Based on jM 500.
    const heightToWidthAspectRation = 0.75; // TODO make dynamic ATM height:width :: 3:4 :: 3/4 = 0.75
    const gridSize = gridRange * cellSize;

    const clusterConfig: ClusterConfig = {
        gridRange,
        maxDivisor,
        cellSize,
        gridSize,
    };

    const clusterStyles: React.CSSProperties = {};

    if (floatingCoords) {
        clusterStyles.position = 'relative';
        clusterStyles.width = `${gridSize / heightToWidthAspectRation}px`;
        clusterStyles.height = `${gridSize}px`;
        clusterStyles.border = '1px solid #fff';
    }

    let colIndex = -1;
    const panels = clusterFields.panels.map((panel: PanelFields, index) => {
        const panelStyles: React.CSSProperties = {};

        const rowIndex = index % gridRange;
        if (rowIndex === 0) {
            colIndex += 1;
        }

        let { x, y, z } = panel;
        if (floatingCoords) {
            panelStyles.position = 'absolute';

            const randomParams = {
                max: cellSize / maxDivisor,
                power10: 1,
            };
            if (forceRandom || typeof x !== 'number') {
                x = cellSize * rowIndex;
                x += getRandomInt(randomParams);
            }
            if (forceRandom || typeof y !== 'number') {
                y = cellSize * heightToWidthAspectRation * colIndex;
                y += getRandomInt(randomParams);
            }
            if (forceRandom || typeof z !== 'number') {
                z = getRandomInt();
            }
            panelStyles.top = `${y}px`;
            panelStyles.left = `${x}px`;
            panelStyles.zIndex = z;
        }

        return {
            panel,
            panelStyles,
        };
    });

    return {
        clusterFields,
        clusterStyles,
        panels,
        panelsTotal,
        clusterConfig,
    };
};

export default clusterCreateObject;
